import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/utils-image";
import { toast } from "sonner";
import {
  History,
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowLeft,
  Loader2,
  Calendar,
} from "lucide-react";

const MONTH_NAMES = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

export default function MonthlyHistoryPage() {
  const [months, setMonths] = useState([]);
  const [currency, setCurrency] = useState("SAR");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r, s] = await Promise.all([
          api.get("/reports/monthly"),
          api.get("/settings"),
        ]);
        setMonths(r.data?.months || []);
        setCurrency(s.data?.currency || "SAR");
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) toast.error("انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى");
        else if (!err?.response) toast.error("تعذر الاتصال بالخادم");
        else toast.error(err?.response?.data?.detail || "تعذر تحميل سجل الشهور");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = useMemo(() => {
    if (!months.length) return { spent: 0, tax: 0, disc: 0, count: 0 };
    return months.reduce(
      (acc, m) => ({
        spent: acc.spent + Number(m.total_expense || 0),
        tax: acc.tax + Number(m.total_tax || 0),
        disc: acc.disc + Number(m.total_discount || 0),
        count: acc.count + Number(m.count || 0),
      }),
      { spent: 0, tax: 0, disc: 0, count: 0 }
    );
  }, [months]);

  return (
    <div className="space-y-8 fade-up" data-testid="monthly-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">التقارير · سجل الشهور</div>
          <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">
            تاريخ الأشهر السابقة
          </h1>
          <p className="text-[#5C605A] mt-2 text-sm sm:text-base">
            راجع رصيد كل شهر، الفائض المرحّل، الإنفاق والإغلاق — نموذج البدل الشهري.
          </p>
        </div>
        <Link
          to="/reports"
          data-testid="monthly-history-back-btn"
          className="hidden sm:inline-flex border border-[#1A1C19]/15 text-[#1F3A33] hover:bg-[#F5F4EF] rounded-md px-5 py-3 font-medium font-readex items-center gap-2 self-start sm:self-auto"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          عودة للتقارير
        </Link>
      </div>

      {/* Summary banner */}
      {!loading && months.length > 0 && (
        <div
          className="relative hero-3d rounded-md p-6 sm:p-8 text-white overflow-hidden shadow-3d-emerald"
          data-testid="history-summary-banner"
        >
          <div className="absolute inset-0 brand-stripes opacity-40" aria-hidden="true" />
          <div className="absolute -bottom-20 -end-20 w-56 h-56 rounded-full bg-[#6A8A76]/25 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative grid grid-cols-1 sm:grid-cols-4 gap-6">
            <SummaryItem label="عدد الأشهر" value={String(months.length)} mono />
            <SummaryItem label="إجمالي التمويل" value={formatCurrency(months.reduce((sum, m) => sum + Number(m.funding_total ?? m.opening_base ?? 0), 0), currency)} mono />
            <SummaryItem label="إجمالي الإنفاق" value={formatCurrency(totals.spent, currency)} mono />
            <SummaryItem label="إجمالي الفواتير" value={String(totals.count)} mono />
          </div>
        </div>
      )}

      {/* Months table */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-md overflow-hidden">
        <div className="flex items-center gap-2 p-5 sm:p-6 border-b border-[#1A1C19]/10">
          <History className="w-5 h-5 text-[#1F3A33]" strokeWidth={1.5} />
          <h2 className="font-readex font-medium text-xl text-[#1A1C19]">جدول الأشهر</h2>
          <span className="text-xs text-[#8A8D86] font-mono-num ms-auto">
            {loading ? "…" : `${months.length} شهر`}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-[#5C605A]">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-readex">جاري التحميل…</span>
          </div>
        ) : months.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="history-table">
                <thead className="bg-[#F5F4EF] text-[#5C605A]">
                  <tr>
                    <Th>الشهر</Th>
                    <Th>الافتتاحي</Th>
                    <Th>الدعم</Th>
                    <Th>المرحّل</Th>
                    <Th>الرصيد المتاح</Th>
                    <Th>الإنفاق</Th>
                    <Th>الإغلاق</Th>
                    <Th center>عدد الفواتير</Th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m, idx) => (
                    <MonthRow key={`${m.year}-${m.month}`} m={m} currency={currency} idx={idx} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[#1A1C19]/10">
              {months.map((m) => (
                <MonthCard key={`${m.year}-${m.month}`} m={m} currency={currency} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryItem({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-white/70 font-readex mb-1.5">{label}</div>
      <div className={`text-2xl sm:text-3xl font-readex font-medium tracking-tight ${mono ? "font-mono-num" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, center }) {
  return (
    <th className={`px-4 py-3 font-readex font-medium text-xs uppercase tracking-wider ${center ? "text-center" : "text-start"}`}>
      {children}
    </th>
  );
}

function MonthRow({ m, currency, idx }) {
  const monthName = MONTH_NAMES[(m.month || 1) - 1];
  const opening = Number(m.opening_balance || 0);
  const carry = Number(m.carry_over || 0);
  const base = Number(m.opening_base ?? opening - carry);
  const spent = Number(m.total_expense || 0);
  const closing = Number(m.closing_balance || 0);

  return (
    <tr
      className={`${idx % 2 === 0 ? "bg-white" : "bg-[#FAF9F5]"} hover:bg-[#F5F4EF] transition-colors`}
      data-testid={`history-row-${m.year}-${m.month}`}
    >
      <td className="px-4 py-3 font-readex">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#8A8D86] flex-shrink-0" strokeWidth={1.5} />
          <span className="font-medium text-[#1A1C19]">{monthName}</span>
          <span className="text-xs text-[#8A8D86] font-mono-num">{m.year}</span>
        </div>
      </td>
      <td className="px-4 py-3 font-mono-num text-[#1A1C19]">{formatCurrency(base, currency)}</td>
      <td className="px-4 py-3 font-mono-num text-[#1F3A33]">{formatCurrency(m.support_total || 0, currency)}</td>
      <td className="px-4 py-3 font-mono-num">
        <CarryBadge value={carry} currency={currency} />
      </td>
      <td className="px-4 py-3 font-mono-num font-medium text-[#1F3A33]">{formatCurrency(opening, currency)}</td>
      <td className="px-4 py-3 font-mono-num text-[#C84C31]">−{formatCurrency(spent, currency)}</td>
      <td className={`px-4 py-3 font-mono-num font-medium ${closing < 0 ? "text-[#C84C31]" : "text-[#1F3A33]"}`}>
        {formatCurrency(closing, currency)}
      </td>
      <td className="px-4 py-3 font-mono-num text-center text-[#5C605A]">{m.count || 0}</td>
    </tr>
  );
}

function MonthCard({ m, currency }) {
  const monthName = MONTH_NAMES[(m.month || 1) - 1];
  const opening = Number(m.opening_balance || 0);
  const carry = Number(m.carry_over || 0);
  const base = Number(m.opening_base ?? opening - carry);
  const spent = Number(m.total_expense || 0);
  const closing = Number(m.closing_balance || 0);

  return (
    <div className="p-4" data-testid={`history-card-${m.year}-${m.month}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#1F3A33]" strokeWidth={1.5} />
          <span className="font-readex font-medium text-[#1A1C19]">{monthName}</span>
          <span className="text-xs text-[#8A8D86] font-mono-num">{m.year}</span>
        </div>
        <span className="text-xs text-[#8A8D86] font-mono-num">{m.count || 0} فاتورة</span>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <CardRow k="الافتتاحي" v={formatCurrency(base, currency)} />
        <CardRow k="الدعم" v={formatCurrency(m.support_total || 0, currency)} />
        <CardRow k="إجمالي التمويل" v={formatCurrency(m.funding_total ?? base, currency)} />
        <CardRow k="المرحّل" v={<CarryBadge value={carry} currency={currency} compact />} />
        <CardRow k="الرصيد المتاح" v={formatCurrency(opening, currency)} strong />
        <CardRow k="الإنفاق" v={`−${formatCurrency(spent, currency)}`} negative />
      </dl>
      <div className="mt-3 pt-3 border-t border-[#1A1C19]/10 flex items-center justify-between">
        <span className="text-xs text-[#5C605A] font-readex">الإغلاق</span>
        <span className={`font-mono-num font-medium text-base ${closing < 0 ? "text-[#C84C31]" : "text-[#1F3A33]"}`}>
          {formatCurrency(closing, currency)}
        </span>
      </div>
    </div>
  );
}

function CardRow({ k, v, strong, negative }) {
  return (
    <>
      <dt className="text-xs text-[#8A8D86] font-readex self-center">{k}</dt>
      <dd className={`font-mono-num text-end ${strong ? "font-medium text-[#1F3A33]" : ""} ${negative ? "text-[#C84C31]" : "text-[#1A1C19]"}`}>{v}</dd>
    </>
  );
}

function CarryBadge({ value, currency, compact }) {
  const v = Number(value || 0);
  if (v === 0) {
    return <span className="text-[#8A8D86]">—</span>;
  }
  const positive = v > 0;
  const cls = positive
    ? "bg-[#E6F2EA] text-[#1F3A33] border-[#A3C4B0]/40"
    : "bg-[#FBE9E3] text-[#C84C31] border-[#E89178]/40";
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs ${cls} ${compact ? "" : ""}`}>
      <Icon className="w-3 h-3" strokeWidth={2} />
      {positive ? "+" : ""}{formatCurrency(v, currency)}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="py-16 px-6 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F5F4EF] text-[#8A8D86] mb-4">
        <Wallet className="w-6 h-6" strokeWidth={1.5} />
      </div>
      <h3 className="font-readex font-medium text-lg text-[#1A1C19] mb-2">لا يوجد سجل بعد</h3>
      <p className="text-sm text-[#5C605A] max-w-sm mx-auto">
        أضف أول فاتورة لبدء تتبّع الرصيد الشهري والفائض المرحّل تلقائياً.
      </p>
      <Link
        to="/add"
        data-testid="history-empty-add-btn"
        className="inline-flex items-center gap-2 mt-4 bg-[#1F3A33] text-white hover:bg-[#172A25] rounded-md px-5 py-2.5 text-sm font-medium font-readex"
      >
        إضافة فاتورة
      </Link>
    </div>
  );
}

